import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { reportDownloadNames } from './Reports.constants';
import { ReportType } from './Reports.types';

// Extended here rather than at an app entry point: this package is consumed by the
// frontend, api, reports and capture, and the filename must not depend on which of
// them happened to load the plugins. `extend` is idempotent.
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Australian Eastern time — the reporting date the lab works to.
 *
 * A zone rather than a fixed +10:00 offset so the date stays correct through
 * daylight saving: over summer eastern Australia is AEDT (+11:00), and a fixed
 * offset would date reports approved between midnight and 1am to the previous day.
 */
const REPORT_TIME_ZONE = 'Australia/Sydney';

/**
 * YYYYMMDD in {@link REPORT_TIME_ZONE}.
 *
 * Deliberately not the host's local date. This name is built both in the browser
 * (AEST/AEDT) and in the reports service (UTC), and those disagree about the date
 * for the first 10-11 hours of every Australian day — a report approved 09:00 AEST
 * is 23:00 UTC the day before. Formatting local-time on each side would put one
 * date on the copy the user downloads and another on the copy emailed to the
 * clinicians and filed into Labmatrix.
 */
function formatDate(value?: string | null): string {
  const date = value ? dayjs(value) : dayjs();

  // `approvedAt` is a timestamp column so this should not happen, but a bad value
  // must not put the literal string `Invalid Date` into an outgoing filename.
  return (date.isValid() ? date : dayjs()).tz(REPORT_TIME_ZONE).format('YYYYMMDD');
}

export interface IReportFileNameParts {
  reportType: ReportType;
  /** Internal patient id (`zcc_patient.patient_id`). */
  patientId: string;
  /** Biomaterial id of the analysis set's primary biosample, if it has one. */
  biomaterialId?: number | null;
  /** From Clinical One. Omitted when `redacted` is true. */
  firstName?: string | null;
  lastName?: string | null;
  /** `zcc_reports.approved_at`; falls back to now when the report is not yet approved. */
  approvedAt?: string | null;
  fileType?: string;
  redacted?: boolean;
  isAmended?: boolean;
}

/**
 * The canonical filename for a finalised report leaving ZeroDash.
 *
 * Shared so that a user downloading the PDF in the browser, the copy emailed to
 * clinicians, and the copy filed into Labmatrix all agree — these were previously
 * only produced in the frontend, and any backend re-implementation would drift.
 */
export function buildReportFileName({
  reportType,
  patientId,
  biomaterialId,
  firstName,
  lastName,
  approvedAt,
  fileType = 'pdf',
  redacted = false,
  isAmended = false,
}: IReportFileNameParts): string {
  const name = reportDownloadNames[reportType];
  const patientName = `_${firstName} ${lastName?.toUpperCase()}`;
  const date = formatDate(approvedAt);

  return `ZERO2_${name}_${patientId}_${biomaterialId}${redacted ? '' : patientName}${isAmended ? '_Amended' : ''}_${date}.${fileType}`;
}
